import React, { Suspense } from "react";
// --- Оборудование: все экраны берём из нового модуля
import {
  EquipmentDashboard,
  EquipmentTsd,
  EquipmentFinger,
  EquipmentDesktop,
  EquipmentTablets,
  EquipmentPcSets,
  EquipmentShipments,
  EquipmentShipped,
  EquipmentTransfers,
  EquipmentRepairQueue,
  EquipmentDefective,
  EquipmentLoans,
} from "../modules/equipment";
import { Box, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { AnimatePresence, motion } from "framer-motion";
import { CleanCard as Card, CleanCardContent as CardContent, CleanCardHeader as CardHeader } from "@/ui";

// --- основные модули проекта
const TmcCalculator = React.lazy(() => import("../modules/tmc-calculator/TmcCalculator"));
const KanbanBoard   = React.lazy(() => import("../modules/kanban/Board"));
const Audit5S       = React.lazy(() => import("../modules/audit5s/Audit5S"));
const VisitsToday   = React.lazy(() => import("../modules/visits/VisitsToday"));
const ProfilePage   = React.lazy(() => import("../modules/profile/ProfilePage"));
const AdminPanel    = React.lazy(() => import("../modules/admin/AdminPanel"));
const ShiftKpi      = React.lazy(() => import("../modules/shift-kpi"));
const ParkingBalance = React.lazy(() => import("../modules/parking-balance/ParkingBalance"));
const DashboardModule = React.lazy(() => import("../modules/dashboard/Dashboard"));
const Analytics = React.lazy(() => import("../modules/analitics/Analytics"));
const AssetsModule = React.lazy(() => import("../modules/assets/AssetsModule"));
const CameraMap = React.lazy(() => import("../modules/camera-map/CameraMap"));
const SortPerformance = React.lazy(() => import("../modules/sort-performance/SortPerformanceModule"));
const ShiftPassport = React.lazy(() => import("../modules/shift-passport/ShiftPassportModule"));
const Incidents = React.lazy(() => import("../modules/incidents/Incidents"));
const AccessRequests = React.lazy(() => import('../modules/access-requests/AccessRequests'));
const JobProfiles = React.lazy(() => import('../modules/job-profiles/JobProfiles'));
const WorkSchedule = React.lazy(() => import('../modules/work-schedule/WorkSchedule'));

type ModuleEntry = React.ComponentType<any>;

const MODULES: Record<string, ModuleEntry> = {
  'access-requests': AccessRequests,
  'job-profiles': JobProfiles,
  'work-schedule': WorkSchedule,
  incidents: Incidents,
  // существующие
  "tmc-calculator": TmcCalculator,
  "tmc": TmcCalculator,
  "kanban": KanbanBoard,
  "tasks": KanbanBoard,
  "board": KanbanBoard,
  "shift-passport": ShiftPassport,
  "audit5s": Audit5S,
  "5s": Audit5S,
  "visits": VisitsToday,

  // оборудование — дашборд и выдача/сдача
  "equipment": EquipmentDashboard,
  "equipment-loans": EquipmentLoans,

  // оборудование — детальные списки (открываются из дашборда)
  "equipment-tsd": EquipmentTsd,
  "equipment-finger_scanners": EquipmentFinger,
  "equipment-desktop_scanners": EquipmentDesktop,
  "equipment-tablets": EquipmentTablets,
  "equipment-pc_sets": EquipmentPcSets,

  // отгрузки
  "equipment-defective": EquipmentDefective,
  "equipment-repair-queue": EquipmentRepairQueue,
  "equipment-shipments": EquipmentShipments,
  "equipment-shipped":   EquipmentShipped,
  "equipment-transfers": EquipmentTransfers,

  // авторизация / профиль / админка
  "profile": ProfilePage,
  "admin":   AdminPanel,

  // аналитика
  "kpi-shifts": ShiftKpi,
  "analytics": Analytics,
  "sort-performance": SortPerformance,

  // логистика
  "parking-balance": ParkingBalance,

  // имущество
  "assets": AssetsModule,
  "camera-map": CameraMap,

  // главная
  "dashboard": DashboardModule,
};

export default function ModuleRouter({ active }: { active?: string }) {
  const Comp = !active ? DashboardModule : MODULES[active];
  if (!Comp) return <UnknownModule id={active!} />;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={active}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.18, ease: [0.22, 0.8, 0.3, 1] }}
      >
        <Suspense fallback={<CenterLoader />}>
          <Comp />
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function CenterLoader() {
  return (
    <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function UnknownModule({ id }: { id: string }) {
  return (
    <Card>
      <CardHeader title="Модуль не найден" />
      <CardContent>
        <Typography variant="body2">Идентификатор: <b>{id}</b></Typography>
        <Typography variant="body2" color="text.secondary">
          Проверьте конфигурацию меню в <code>Shell</code>.
        </Typography>
      </CardContent>
    </Card>
  );
}
